import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Rss, Plus, Search, Sparkles, Save, Rocket, Archive, Eye, Loader2,
    Copy, ChevronLeft, X,
} from "lucide-react";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";

const STATUS_CHIP = {
    draft: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    pending_review: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    published: "text-[var(--color-success)] bg-[var(--color-success)]/10 border-[var(--color-success)]/40",
    archived: "text-text-secondary bg-white/5 border-white/10",
};

function TemplatePicker({ templates, value, onChange }) {
    return (
        <div className="grid gap-2 md:grid-cols-3" data-testid="template-picker">
            {templates.map((t) => (
                <button
                    key={t.key}
                    type="button"
                    data-testid={`template-${t.key}`}
                    onClick={() => onChange(t.key)}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                        value === t.key
                            ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                            : "border-[var(--color-border)] hover:border-[var(--color-primary)]/40"
                    }`}
                >
                    <div className="text-sm font-medium text-text-main">{t.label}</div>
                    <div className="text-[10px] text-text-secondary mt-1">{t.description}</div>
                </button>
            ))}
        </div>
    );
}

function LandingList({ items, onSelect, onCreate, filters, setFilters }) {
    return (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
             data-testid="mini-landing-list">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                        Phase 10 · V30
                    </div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-text-main">Mini-landings</h1>
                </div>
                <button
                    onClick={onCreate}
                    data-testid="mini-landing-create"
                    className="text-sm rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-2"
                >
                    <Plus className="h-4 w-4" /> New landing
                </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-secondary" />
                    <input
                        type="text" placeholder="Search title or slug…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        data-testid="mini-landing-search"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:border-[var(--color-primary)]/50"
                    />
                </div>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    data-testid="mini-landing-status-filter"
                    className="px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main"
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending review</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                </select>
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-text-secondary py-6 text-center">
                    No mini-landings yet. Create your first via the “New landing” button — an AI draft is a click away.
                </p>
            ) : (
                <ul className="divide-y divide-[var(--color-border)]/40">
                    {items.map((it) => (
                        <li key={it.id}
                            data-testid={`mini-landing-row-${it.id}`}
                            className="flex items-center gap-3 py-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_CHIP[it.status] || STATUS_CHIP.draft}`}>
                                        {it.status}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                        {it.template_used}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                        {it.target_language}
                                    </span>
                                </div>
                                <div className="text-sm text-text-main mt-1 truncate">{it.title}</div>
                                <div className="text-[11px] text-text-secondary">/landing/{it.slug}</div>
                            </div>
                            <button
                                onClick={() => onSelect(it.id)}
                                data-testid={`mini-landing-edit-${it.id}`}
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5"
                            >
                                Open
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function LandingEditor({ item, onBack, onChange, onSave, onPublish, onArchive, onAiDraft,
                          templates, busy }) {
    const [tab, setTab] = useState("edit");
    return (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
             data-testid="mini-landing-editor">
            <div className="flex items-center gap-3 mb-4">
                <button onClick={onBack} data-testid="mini-landing-back"
                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1">
                    <ChevronLeft className="h-3 w-3" /> Back
                </button>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                        Editing mini-landing
                    </div>
                    <h1 className="text-xl font-semibold text-text-main truncate">{item.title || "Untitled"}</h1>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_CHIP[item.status] || STATUS_CHIP.draft}`}>
                    {item.status}
                </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={onAiDraft} disabled={busy}
                        data-testid="mini-landing-ai-draft"
                        className="text-xs rounded-full border border-[var(--color-secondary)]/40 text-[var(--color-secondary)] px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                    <Sparkles className="h-3 w-3" /> AI draft
                </button>
                <button onClick={onSave} disabled={busy}
                        data-testid="mini-landing-save"
                        className="text-xs rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                    <Save className="h-3 w-3" /> Save
                </button>
                <button onClick={onPublish} disabled={busy || item.status === "published"}
                        data-testid="mini-landing-publish"
                        className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                    <Rocket className="h-3 w-3" /> Publish
                </button>
                <a href={`/landing/${item.slug}?preview=true`} target="_blank" rel="noreferrer"
                    data-testid="mini-landing-preview"
                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Preview
                </a>
                {item.status !== "archived" && (
                    <button onClick={onArchive} disabled={busy}
                            data-testid="mini-landing-archive"
                            className="text-xs rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                        <Archive className="h-3 w-3" /> Archive
                    </button>
                )}
            </div>

            <div className="flex gap-2 mb-4 border-b border-[var(--color-border)]/40">
                {[
                    { k: "edit", label: "Edit" },
                    { k: "template", label: "Template" },
                    { k: "seo", label: "SEO" },
                    { k: "preview", label: "Live preview" },
                ].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)}
                        data-testid={`mini-landing-tab-${t.k}`}
                        className={`text-xs px-3 py-2 -mb-px border-b-2 ${
                            tab === t.k
                                ? "border-[var(--color-primary)] text-text-main"
                                : "border-transparent text-text-secondary"
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "edit" && (
                <div className="grid gap-3">
                    {[
                        ["title", "Internal title", "text"],
                        ["hero_headline", "Hero headline", "text"],
                        ["hero_subheadline", "Hero subheadline", "text"],
                        ["cta_text", "CTA text", "text"],
                        ["cta_url", "CTA URL", "text"],
                        ["target_persona", "Target persona", "text"],
                        ["target_language", "Language (ISO)", "text"],
                    ].map(([k, label]) => (
                        <label key={k} className="block">
                            <span className="text-[10px] uppercase tracking-widest text-text-secondary">{label}</span>
                            <input
                                value={item[k] || ""}
                                onChange={(e) => onChange({ [k]: e.target.value })}
                                data-testid={`mini-landing-field-${k}`}
                                className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main"
                            />
                        </label>
                    ))}
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                            Body (markdown)
                        </span>
                        <textarea
                            value={item.body_markdown || ""}
                            onChange={(e) => onChange({ body_markdown: e.target.value })}
                            rows={14}
                            data-testid="mini-landing-field-body_markdown"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main font-mono resize-y"
                        />
                    </label>
                </div>
            )}

            {tab === "template" && (
                <TemplatePicker
                    templates={templates}
                    value={item.template_used}
                    onChange={(k) => onChange({ template_used: k })}
                />
            )}

            {tab === "seo" && (
                <div className="grid gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">SEO title</span>
                        <input
                            value={(item.seo_meta || {}).title || ""}
                            onChange={(e) => onChange({
                                seo_meta: { ...(item.seo_meta || {}), title: e.target.value },
                            })}
                            data-testid="mini-landing-seo-title"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">SEO description</span>
                        <textarea
                            value={(item.seo_meta || {}).description || ""}
                            onChange={(e) => onChange({
                                seo_meta: { ...(item.seo_meta || {}), description: e.target.value },
                            })}
                            rows={3}
                            data-testid="mini-landing-seo-description"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Keywords (comma sep.)</span>
                        <input
                            value={((item.seo_meta || {}).keywords || []).join(", ")}
                            onChange={(e) => onChange({
                                seo_meta: {
                                    ...(item.seo_meta || {}),
                                    keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                                },
                            })}
                            data-testid="mini-landing-seo-keywords"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main"
                        />
                    </label>
                </div>
            )}

            {tab === "preview" && (
                <div className="rounded-lg border border-[var(--color-border)] p-6 bg-[var(--color-panel-ai)]"
                     data-testid="mini-landing-live-preview">
                    <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                        {item.hero_headline || "Untitled"}
                    </h1>
                    {item.hero_subheadline && (
                        <p className="text-text-secondary mt-3">{item.hero_subheadline}</p>
                    )}
                    <pre className="text-xs text-text-secondary whitespace-pre-wrap mt-4 max-h-72 overflow-auto">
                        {item.body_markdown || "…"}
                    </pre>
                    <a href={item.cta_url || "#"}
                       className="inline-flex mt-4 text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium">
                        {item.cta_text || "Get started"}
                    </a>
                </div>
            )}
        </div>
    );
}

function AiDraftModal({ open, onClose, onDraft, templates }) {
    const [payload, setPayload] = useState({
        template: "product_launch",
        target_persona: "B2B founder",
        keywords: "lead generation, AI automation",
        tone: "confident, expert, no marketing fluff",
        target_language: "en",
    });
    const [busy, setBusy] = useState(false);
    if (!open) return null;
    const run = async () => {
        setBusy(true);
        try {
            const kw = payload.keywords.split(",").map((s) => s.trim()).filter(Boolean);
            const draft = await api.adminMiniLandingAiDraft({
                template: payload.template,
                target_persona: payload.target_persona,
                keywords: kw,
                tone: payload.tone,
                target_language: payload.target_language,
            });
            onDraft(draft.draft, payload.template);
        } catch (e) {
            toast.error(e.message || "AI draft failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-[var(--color-card)] border border-[var(--color-border)] p-6"
                 data-testid="mini-landing-ai-draft-modal">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-text-main">AI draft</h2>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-main">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="grid gap-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Template</span>
                        <select value={payload.template}
                            onChange={(e) => setPayload({ ...payload, template: e.target.value })}
                            data-testid="ai-draft-template"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main">
                            {templates.map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Target persona</span>
                        <input value={payload.target_persona}
                            onChange={(e) => setPayload({ ...payload, target_persona: e.target.value })}
                            data-testid="ai-draft-persona"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Keywords (comma sep.)</span>
                        <input value={payload.keywords}
                            onChange={(e) => setPayload({ ...payload, keywords: e.target.value })}
                            data-testid="ai-draft-keywords"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">Language (ISO)</span>
                        <input value={payload.target_language}
                            onChange={(e) => setPayload({ ...payload, target_language: e.target.value })}
                            data-testid="ai-draft-language"
                            className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--color-panel-ai)] border border-[var(--color-border)] text-sm text-text-main" />
                    </label>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose}
                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2">
                        Cancel
                    </button>
                    <button onClick={run} disabled={busy}
                        data-testid="ai-draft-generate"
                        className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-1 disabled:opacity-50">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        Generate draft
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminMiniLandings() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filters, setFilters] = useState({ q: "", status: "" });
    const [busy, setBusy] = useState(false);
    const [aiModal, setAiModal] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (filters.q) params.q = filters.q;
            if (filters.status) params.status = filters.status;
            const [list, tpls] = await Promise.all([
                api.adminMiniLandingsList(params),
                templates.length === 0 ? api.adminMiniLandingTemplates() : Promise.resolve({ templates }),
            ]);
            setItems(list.items || []);
            if (!templates.length) setTemplates(tpls.templates || []);
        } catch (e) {
            toast.error(e.message || "Failed to load mini-landings");
        }
    }, [filters.q, filters.status, templates.length]);

    useEffect(() => { load(); }, [load]);

    const openEditor = async (id) => {
        try {
            const row = await api.adminMiniLandingGet(id);
            setSelected(row);
        } catch (e) { toast.error(e.message || "Failed to load"); }
    };

    const startNew = async () => {
        setBusy(true);
        try {
            const created = await api.adminMiniLandingCreate({
                template: "custom",
                title: "New landing",
                hero_headline: "Bold new headline",
                hero_subheadline: "One-line hook that sells the value.",
                body_markdown: "## Section\n\nEdit me.\n",
                cta_text: "Get started",
                cta_url: "/app/subscription",
                target_persona: "",
                target_language: "en",
            });
            setSelected(created);
            toast.success("Draft created");
            await load();
        } catch (e) { toast.error(e.message || "Create failed"); }
        finally { setBusy(false); }
    };

    const applyEdit = (patch) => {
        setSelected((cur) => ({ ...cur, ...patch }));
    };

    const save = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            const row = await api.adminMiniLandingUpdate(selected.id, {
                title: selected.title, hero_headline: selected.hero_headline,
                hero_subheadline: selected.hero_subheadline,
                body_markdown: selected.body_markdown, cta_text: selected.cta_text,
                cta_url: selected.cta_url, target_persona: selected.target_persona,
                target_language: selected.target_language,
                template_used: selected.template_used, seo_meta: selected.seo_meta,
            });
            setSelected(row);
            toast.success("Saved");
            load();
        } catch (e) { toast.error(e.message || "Save failed"); }
        finally { setBusy(false); }
    };

    const publish = async () => {
        setBusy(true);
        try {
            const row = await api.adminMiniLandingPublish(selected.id);
            setSelected(row);
            toast.success("Published — visible at /landing/" + row.slug);
            load();
        } catch (e) {
            const msg = e.message || "";
            if (msg.includes("human_review_required")) {
                toast.error("Edit the title/body/hero before publishing (human-review gate).");
            } else {
                toast.error(msg || "Publish failed");
            }
        } finally { setBusy(false); }
    };

    const archive = async () => {
        if (!window.confirm("Archive this landing?")) return;
        setBusy(true);
        try {
            const row = await api.adminMiniLandingArchive(selected.id, "manual archive");
            setSelected(row);
            toast.success("Archived");
            load();
        } catch (e) { toast.error(e.message || "Archive failed"); }
        finally { setBusy(false); }
    };

    const applyDraft = async (draft, template) => {
        setAiModal(false);
        if (selected) {
            // fill current draft
            applyEdit({
                title: draft.title, hero_headline: draft.hero_headline,
                hero_subheadline: draft.hero_subheadline, body_markdown: draft.body_markdown,
                cta_text: draft.cta_text, cta_url: draft.cta_url,
                template_used: template, seo_meta: draft.seo_meta,
            });
            toast.success("AI draft applied — remember to save.");
        } else {
            // create new from AI draft
            try {
                const created = await api.adminMiniLandingCreate({
                    template,
                    title: draft.title,
                    hero_headline: draft.hero_headline,
                    hero_subheadline: draft.hero_subheadline,
                    body_markdown: draft.body_markdown,
                    cta_text: draft.cta_text,
                    cta_url: draft.cta_url,
                    target_language: "en",
                    seo_meta: draft.seo_meta,
                });
                setSelected(created);
                toast.success("New AI-drafted landing created");
                load();
            } catch (e) { toast.error(e.message || "Create failed"); }
        }
    };

    if (!isAdminRole(me?.role)) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center">
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full space-y-6"
                        data-testid="admin-mini-landings">
                    {!selected ? (
                        <LandingList
                            items={items}
                            onSelect={openEditor}
                            onCreate={() => setAiModal(true)}
                            filters={filters}
                            setFilters={setFilters}
                        />
                    ) : (
                        <LandingEditor
                            item={selected}
                            templates={templates}
                            onBack={() => setSelected(null)}
                            onChange={applyEdit}
                            onSave={save}
                            onPublish={publish}
                            onArchive={archive}
                            onAiDraft={() => setAiModal(true)}
                            busy={busy}
                        />
                    )}
                </main>
            </div>
            <AiDraftModal
                open={aiModal}
                onClose={() => setAiModal(false)}
                onDraft={applyDraft}
                templates={templates}
            />
        </div>
    );
}
